import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, Badge, Button, Input, Field, Switch, Segmented, SectionTitle } from '../components/ui';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface ClientOpt { id: number; name: string }
interface LinkRow { url: string; tracking_code: string }
interface Item { url?: string; link?: string; type?: string; tracking_code?: string }
interface MappingRow { source_field?: string; target_field?: string; target_label?: string; sample_value?: unknown; confidence?: string; notes?: string }
interface FeedMapping {
  mappings?: MappingRow[]; unmapped_source_fields?: string[]; unmapped_target_fields?: string[];
  feed_format?: string; total_source_fields?: number; total_target_fields?: number;
}
interface DestData {
  id?: string; name?: string; type?: string; feed_url?: string; client_id?: number | string | null;
  tracking_code?: string; status?: string; items?: Item[]; feed_mapping?: FeedMapping | null;
}

const CONF_COLOR: Record<string, string> = { high: 'text-emerald-600', medium: 'text-amber-500', low: 'text-rose-600' };

export default function DestinationForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [type, setType] = useState<'links' | 'feed'>('links');
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('active');
  const [trackingCode, setTrackingCode] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [links, setLinks] = useState<LinkRow[]>([{ url: '', tracking_code: '' }]);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // feed + IA
  const [mapping, setMapping] = useState<FeedMapping | null>(null);
  const [sampleCount, setSampleCount] = useState<number | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [feedBusy, setFeedBusy] = useState<string | null>(null);
  const [feedMsg, setFeedMsg] = useState<string | null>(null);

  useEffect(() => {
    api.get<ApiResponse<ClientOpt[]>>('/clients/list').then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get<ApiResponse<DestData>>(`/destinations/get?id=${id}`)
      .then((r) => {
        const d = r.data || {};
        setName(d.name || '');
        setType((d.type as 'links' | 'feed') || 'links');
        setClientId(d.client_id != null ? String(d.client_id) : '');
        setStatus(d.status || 'active');
        setTrackingCode(d.tracking_code || '');
        setFeedUrl(d.feed_url || '');
        const manual = (d.items || []).filter((it) => it.type === 'link');
        setLinks(manual.length ? manual.map((it) => ({ url: it.url || it.link || '', tracking_code: it.tracking_code || '' })) : [{ url: '', tracking_code: '' }]);
        // mapping salvo vem junto no /destinations/get (coluna feed_mapping)
        if (d.feed_mapping && (d.feed_mapping as FeedMapping).mappings) setMapping(d.feed_mapping as FeedMapping);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  async function save() {
    setSaving(true); setError(null);
    const payload: Record<string, unknown> = {
      id: id || undefined,
      name,
      type,
      client_id: clientId ? Number(clientId) : null,
      status,
      tracking_code: trackingCode || null,
    };
    if (type === 'feed') {
      payload.feed_url = feedUrl || null;
    } else {
      const clean = links.filter((l) => l.url.trim()).map((l) => ({ url: l.url.trim(), tracking_code: l.tracking_code.trim() || null }));
      if (clean.length === 0) { setError('Add at least 1 link'); setSaving(false); return; }
      payload.links = clean;
    }
    try {
      const r = await api.post<ApiResponse<unknown>>('/destinations/save', payload);
      if (r.success) navigate('/destinations'); else setError(r.error || r.message || 'Failed to save');
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Failed to save'); } finally { setSaving(false); }
  }

  const updLink = (i: number, k: keyof LinkRow, v: string) => { const next = links.map((l) => ({ ...l })); next[i][k] = v; setLinks(next); };

  // ===== Feed actions (espelham o SSR) =====
  async function processFeed() {
    if (!feedUrl.trim()) { alert('Please enter a feed URL'); return; }
    if (!id) { alert('Save the destination first'); return; }
    if (!confirm('This will replace all existing feed items. Continue?')) return;
    setFeedBusy('process'); setFeedMsg(null);
    try {
      const r = await api.post<ApiResponse<{ itemsAdded?: number }>>('/destinations/feed/process', { destination_set_id: id, feed_url: feedUrl.trim() });
      setFeedMsg(r.success ? `Feed processed: ${r.data?.itemsAdded ?? 0} items imported.` : (r.error || 'Failed to process the feed'));
    } catch (e) { setFeedMsg(e instanceof ApiError ? e.message : 'Failed to process the feed'); }
    finally { setFeedBusy(null); }
  }

  async function mapFeedWithAI() {
    if (!feedUrl.trim()) { alert('Please enter a feed URL first'); return; }
    setMapLoading(true); setMapError(null); setFeedBusy('map');
    try {
      const r = await api.post<ApiResponse<{ mapping: FeedMapping; sample_count?: number }>>('/destinations/feed/map', { destination_set_id: id || null, feed_url: feedUrl.trim() });
      if (r.success && r.data?.mapping) { setMapping(r.data.mapping); setSampleCount(r.data.sample_count ?? null); }
      else setMapError(r.message || r.error || 'Failed to map the feed');
    } catch (e) { setMapError(e instanceof ApiError ? e.message : 'Failed to map the feed'); }
    finally { setMapLoading(false); setFeedBusy(null); }
  }

  async function clearFeed() {
    if (!id) { alert('Save the destination first'); return; }
    if (!confirm('Are you sure you want to delete ALL items from this feed? This action cannot be undone.')) return;
    setFeedBusy('clear'); setFeedMsg(null);
    try {
      const r = await api.post<ApiResponse<unknown> & { deleted?: number }>('/destinations/feed/clear', { destination_set_id: id });
      setFeedMsg(r.success ? `Feed cleared: ${r.deleted ?? 0} items removed.` : 'Failed to clear the feed');
    } catch (e) { setFeedMsg(e instanceof ApiError ? e.message : 'Failed to clear the feed'); }
    finally { setFeedBusy(null); }
  }

  const sampleStr = (v: unknown) => {
    if (v == null) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return s.length > 60 ? s.slice(0, 60) + '…' : s;
  };

  if (loading) return <Layout title="Destination"><Card className="p-10 text-center text-slate-400">Loading…</Card></Layout>;

  return (
    <Layout title={isEdit ? 'Edit Destination Set' : 'Add Destination Set'} actions={<><Button variant="outline" onClick={() => navigate('/destinations')}>Cancel</Button><Button onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Update' : 'Create')}</Button></>}>
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}

      <Card className="p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Name" className="md:col-span-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Destination set name" /></Field>
          <Field label="Status"><div className="pt-1"><Switch checked={status === 'active'} onChange={(v) => setStatus(v ? 'active' : 'inactive')} labelOn="active" labelOff="inactive" /></div></Field>
          <Field label="Type"><Segmented value={type} onChange={(v) => setType(v)} options={[{ value: 'links', label: 'Links' }, { value: 'feed', label: 'Feed' }]} /></Field>
          <Field label="Client" className="md:col-span-2">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100">
              <option value="">None</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Tracking Code (default)" help="applied to feed products and to links without their own code" className="md:col-span-3">
            <Input value={trackingCode} onChange={(e) => setTrackingCode(e.target.value)} placeholder="utm_source=google&utm_medium=cpc" />
          </Field>
        </div>
      </Card>

      {type === 'feed' ? (
        <>
        <Card className="p-5 mb-5">
          <SectionTitle>Feed</SectionTitle>
          <Field label="Feed URL" help="XML feed URL in Google Shopping format">
            <Input value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} placeholder="https://example.com/feed.xml" />
          </Field>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button onClick={processFeed} disabled={!!feedBusy}>{feedBusy === 'process' ? '⏳ Processing…' : '↻ Process Feed'}</Button>
            <Button className="bg-sky-500 hover:bg-sky-600" onClick={mapFeedWithAI} disabled={!!feedBusy}>{feedBusy === 'map' ? '⏳ Mapping…' : '⛃ Map Feed (AI)'}</Button>
            <Button variant="danger" onClick={clearFeed} disabled={!!feedBusy}>🗑 Clear Feed</Button>
          </div>
          {feedMsg && <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-2">{feedMsg}</div>}
        </Card>

        {(mapLoading || mapError || mapping) && (
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-sky-50/60">
              <span className="text-sm font-semibold text-sky-800">⛃ Feed Field Mapping <span className="font-normal text-sky-600">(AI-powered)</span></span>
              {mapping && <Badge tone="green">✓ Mapped</Badge>}
            </div>

            {mapLoading && (
              <div className="p-10 text-center">
                <div className="h-10 w-10 mx-auto rounded-full border-2 border-sky-400 border-t-transparent animate-spin mb-3" />
                <p className="text-sm text-slate-600"><strong>AI</strong> is analyzing your feed fields…</p>
                <p className="text-xs text-slate-400 mt-1">Sampling up to 5 items to detect the data pattern</p>
              </div>
            )}

            {mapError && <div className="p-4"><div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">✕ {mapError}</div></div>}

            {mapping && !mapLoading && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-border">
                  <Badge tone="blue">{mapping.feed_format || 'Unknown Format'}</Badge>
                  <small className="text-slate-400 text-xs">
                    ℹ {(mapping.mappings || []).length} fields mapped | Analyzed {sampleCount ?? '?'} sample items | {mapping.total_source_fields ?? '?'} source fields | {mapping.total_target_fields ?? '?'} target fields
                  </small>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-slate-500 bg-sky-50/60">
                        <th className="px-4 py-2.5 font-medium w-[25%]">⇧ Source Field (Feed)</th>
                        <th className="px-2 py-2.5 font-medium w-[8%] text-center"></th>
                        <th className="px-4 py-2.5 font-medium w-[25%]">⇩ Target Field (System)</th>
                        <th className="px-4 py-2.5 font-medium w-[22%]">Sample Value</th>
                        <th className="px-4 py-2.5 font-medium w-[10%] text-center">Confidence</th>
                        <th className="px-4 py-2.5 font-medium w-[10%]">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(mapping.mappings || []).map((m, i) => (
                        <tr key={i} className="align-middle">
                          <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-700">{m.source_field || <em className="text-slate-300">-</em>}</td>
                          <td className="px-2 py-2 text-center text-sky-500">➜</td>
                          <td className="px-4 py-2">
                            <span className="font-mono text-xs font-semibold text-emerald-600">{m.target_field || '-'}</span>
                            {m.target_label && <div className="text-[11px] text-slate-400">{m.target_label}</div>}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-500 max-w-[200px] truncate" title={typeof m.sample_value === 'object' ? JSON.stringify(m.sample_value) : String(m.sample_value ?? '')}>{sampleStr(m.sample_value)}</td>
                          <td className={`px-4 py-2 text-center text-xs font-bold ${CONF_COLOR[m.confidence || 'low'] || 'text-slate-400'}`}>{(m.confidence || '-').toUpperCase()}</td>
                          <td className="px-4 py-2 text-[11px] text-slate-400">{m.notes || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {((mapping.unmapped_source_fields?.length ?? 0) > 0 || (mapping.unmapped_target_fields?.length ?? 0) > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border-t border-border">
                    {(mapping.unmapped_source_fields?.length ?? 0) > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-slate-500 mb-1.5">⚠ Unmapped Source Fields</h5>
                        <div className="flex flex-wrap gap-1.5">{mapping.unmapped_source_fields!.map((f) => <Badge key={f} tone="neutral">{f}</Badge>)}</div>
                      </div>
                    )}
                    {(mapping.unmapped_target_fields?.length ?? 0) > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-rose-600 mb-1.5">Unmapped Target Fields</h5>
                        <div className="flex flex-wrap gap-1.5">{mapping.unmapped_target_fields!.map((f) => <Badge key={f} tone="amber">{f}</Badge>)}</div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Card>
        )}
        </>
      ) : (
        <Card className="p-5">
          <SectionTitle>Links</SectionTitle>
          {/* Cabeçalho das colunas */}
          <div className="flex items-center gap-2 px-1 pb-1.5 mb-1 border-b border-border">
            <div className="flex-[1_1_45%] text-[11px] font-semibold uppercase tracking-wide text-slate-500">URL</div>
            <div className="flex-[1_1_55%] text-[11px] font-semibold uppercase tracking-wide text-slate-500">Tracking Code / UTM <span className="normal-case font-normal text-slate-400">(optional — inherits the default)</span></div>
            <div className="w-7" />
          </div>
          <div className="space-y-2">
            {links.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-[1_1_45%]"><Input value={l.url} onChange={(e) => updLink(i, 'url', e.target.value)} placeholder="https://example.com/page" /></div>
                <div className="flex-[1_1_55%]"><Input value={l.tracking_code} onChange={(e) => updLink(i, 'tracking_code', e.target.value)} placeholder="?utm_source=…&utm_medium=cpc&utm_campaign=…" className="font-mono text-xs" /></div>
                <button type="button" onClick={() => setLinks(links.length > 1 ? links.filter((_, j) => j !== i) : [{ url: '', tracking_code: '' }])} className="w-7 shrink-0 text-rose-500 hover:text-rose-700 text-lg">×</button>
              </div>
            ))}
          </div>
          <Button variant="outline" className="mt-3" onClick={() => setLinks([...links, { url: '', tracking_code: '' }])}>+ Add Link</Button>
        </Card>
      )}
    </Layout>
  );
}
